import React from 'react';
import { useState, useEffect } from 'react';
import { useDominoLoginFlag } from '@domui-hooks/useDominoLoginFlag';
import { Text, RichText, withSitecoreContext, Image } from '@sitecore-jss/sitecore-jss-react';
import { LinkCard as ContentPageCardNPM } from '@rotaryintl/component-landing-pages';
import { createLink, tokenizeLink, createLanguageLink, tokenizeText } from '../../utilities/link-helpers';
import Loading from '@components/Loading';

/**
 * Loads Rotary Individual Token based on Domino Flag
 * @param {*} isDominoLoggedIn 
 * @returns individualToken 
 */
async function loadIndividualToken(isDominoLoggedIn) {
  let individualToken = {};

  if (isDominoLoggedIn === true) {
    const { MyRotary_getTokens: getDomuiIndividualToken } = await import('@domui-sitecore/utilities/helper-functions');
    individualToken = await getDomuiIndividualToken();
  } else if (isDominoLoggedIn === false) {
    const { MyRotary_getTokens: getIndividualToken } = await import('@sitecore/utilities/helper-functions');
    individualToken = await getIndividualToken();
  }

  return individualToken;
}

/**
 * Loads Rotary all clubs based on Domino Flag
 * @param {*} isDominoLoggedIn 
 * @returns allClubs 
 */
async function loadAllClubs(isDominoLoggedIn) {
  let allClubs = []

  if (isDominoLoggedIn === true) {
    const { MyRotary_getAllClubs: getDomuiAllClubs } = await import('@domui-sitecore/utilities/helper-functions');
    allClubs = await getDomuiAllClubs();
  } else if (isDominoLoggedIn === false) {
    const { MyRotary_getAllClubs: getAllClubs } = await import('@sitecore/utilities/helper-functions');
    allClubs = await getAllClubs();
  }

  return allClubs;
}

/**
 * Loads Rotary all districts based on Domino Flag
 * @param {*} isDominoLoggedIn 
 * @returns allDistricts 
 */
async function loadAllDistricts(isDominoLoggedIn) {
  let allDistricts = []

  if (isDominoLoggedIn === true) {
    const { MyRotary_getAllDistrcits: getDomuiAllDistrcits } = await import('@domui-sitecore/utilities/helper-functions');
    allDistricts = await getDomuiAllDistrcits();
  } else if (isDominoLoggedIn === false) {
    const { MyRotary_getAllDistrcits: getAllDistricts } = await import('@sitecore/utilities/helper-functions');
    allDistricts = await getAllDistricts();
  }

  return allDistricts;
}

/**
 * Converts link to Domino Link if Flag is on
 * @param {*} url 
 * @param {*} dominoLoginFlag 
 * @returns link
 */
const convertToDomuiPath = (url, linkType, dominoLoginFlag) => {
    if(!url) return '';
    if (!dominoLoginFlag) return url;
    if (/\/domui\//i.test(url)) return url;
    
    if(linkType === 'club' || linkType === 'district' || linkType === 'allclubs' || linkType === 'alldistricts'){
      return url.replace(/^\/([a-z]{2})\//i, '/$1/domui/');
    }else{
      return url;
    }
}

const ContentPageCard = ({ sitecoreContext, fields: { CardLinkType, Title, Description, Link, Image: ImageField, IsFeatured } = {} }) => {
  const contextLanguage = sitecoreContext?.language.toLowerCase();

  const [tokens, setTokens] = useState({});
  const { data: dominoLoginFlag } = useDominoLoginFlag();

  useEffect(async () => {
    const tokensResponse = await loadIndividualToken(dominoLoginFlag);
    setTokens(tokensResponse);
  }, [dominoLoginFlag]);

  const [allClubs, setAllClubs] = useState([]);
  useEffect(async () => {
    const response = await loadAllClubs(dominoLoginFlag);
    setAllClubs(response);
  }, [dominoLoginFlag]);

  const [allDistricts, setAllDistricts] = useState([]);
  useEffect(async () => {
    const response = await loadAllDistricts(dominoLoginFlag);
    setAllDistricts(response);
  }, [dominoLoginFlag]);

  if (dominoLoginFlag === null) {
    return <Loading />;
  }

  const linkType = CardLinkType?.fields?.Value?.value;
  const cardTitle = () => {
    if (linkType === 'club' || linkType === 'district') {
      return tokenizeText(Title?.value, tokens).text;
    } else {
      return <Text field={Title} />;
    }
  };

  const cardLink = tokenizeLink(createLink(Link, Title), tokens)
  const convertedLink = convertToDomuiPath(cardLink?.linkUrl, linkType, dominoLoginFlag);
  const sanitizedCardLink = {...cardLink, linkUrl: convertedLink}

  if ((linkType === 'allclubs' && (!allClubs || allClubs.length <= 1)) || (linkType === 'alldistricts' && (!allDistricts || allDistricts.length <= 1))) {
    return <></>;
  } else {
    return (
      <ContentPageCardNPM
        colSpan={IsFeatured?.value ? '2' : '1'}
        link={sanitizedCardLink}
        title={cardTitle()}
        description={<RichText field={Description} />}
        image={ImageField?.value?.src && !ImageField?.value?.src.includes('default_image.svg') && <Image field={ImageField} />}
        backgroundColor="white"
      />
    );
  }
};

export default withSitecoreContext()(ContentPageCard);